import React, { useEffect, useMemo } from 'react';

import Button from 'shared/components/andtComponents/Button.js';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import { useRecommendationsNewContext } from 'recommendationsNew/contexts/recommendationsNewContext.jsx';
import useSaveChart from 'recommendationsNew/hooks/react-query/useSaveChart.js';
import { DisplayMetricTypes } from 'usage/constants/costAndUsageConstants.js';
import { useUserSettingsContext } from 'users/utils/contexts/UserSettingsContext.jsx';
import BarsChart from 'recommendationsNew/components/shared/barsChart/BarsChart.tsx';
import Spinner from 'shared/components/andtComponents/Spinner';
import { DataChart, TransformedChartData, WasteTrackerChartProps } from '../../../types.ts';
import { useHeatMapContext } from '../contexts/heatMapContext';
import styles from './wasteTrackerChart.module.scss';

const barGradient = (
  <defs>
    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#27C9BE" />
      <stop offset="95.5%" stopColor="#1A896A" />
    </linearGradient>
  </defs>
);

const dataKeys = ['totalSavings'];
const dataProperties = { totalSavings: { fill: 'url(#barGradient)' } };
const IconComponent = ({ iconName }: { iconName: string }) => <GenerateIcon iconName={iconName} />;

const QuarterlyIcon = () => <IconComponent iconName={ICONS.circleQuarterStroke.name} />;
const MonthlyIcon = () => <IconComponent iconName={ICONS.calendar.name} />;
const DailyIcon = () => <IconComponent iconName={ICONS.calendarDay.name} />;

const WasteTrackerChart: React.FC<WasteTrackerChartProps> = ({ externalData, externalDateGranularity }) => {
  const { numStrAbriviaionByDisplayMetric } = useUserSettingsContext();

  const { recommendationFilters: filtersContext, recommendationExternalFilters: externalFiltersContext } =
    useRecommendationsNewContext();
  const { dateGranularity, setDateGranularity } = useHeatMapContext();
  const { fetchSaveChart } = useSaveChart();

  const { data, isLoading } = fetchSaveChart({
    filters: filtersContext,
    externalFilters: externalFiltersContext,
    dateGranularity,
    isEnabled: !externalData,
  });

  const savingsData = useMemo(() => {
    if (!data && !externalData) {
      return null;
    }
    return data ?? externalData;
  }, [data, externalData]);

  const chartData: TransformedChartData[] = useMemo(() => {
    if (!savingsData || isLoading) {
      return [];
    }
    return savingsData.map((item: DataChart) => ({
      usageDate: item.processing_date,
      totalSavings: item.recommendations_current - item.recommended,
    }));
  }, [savingsData, isLoading]);

  useEffect(() => {
    if (externalDateGranularity && externalDateGranularity !== dateGranularity) {
      setDateGranularity(externalDateGranularity);
    }
  }, [externalDateGranularity, dateGranularity, setDateGranularity]);

  return (
    <div className={styles.wasteTrackerChartContainer}>
      <div className={styles.headerRow}>
        <span className={styles.header}>Accumulated Savings </span>
        {!externalData && (
          <div className={styles.swaggerWrapper}>
            <Button
              isSecondary={dateGranularity !== 'daily'}
              overrideStyles={{ height: '24px', borderRadius: '4px' }}
              text="Daily"
              onClick={() => setDateGranularity('daily')}
              icon={DailyIcon}
            />
            <Button
              isSecondary={dateGranularity !== 'monthly'}
              overrideStyles={{ height: '24px', borderRadius: '4px' }}
              text="Monthly"
              onClick={() => setDateGranularity('monthly')}
              icon={MonthlyIcon}
            />
            <Button
              isSecondary={dateGranularity !== 'quarterly'}
              text="Quarterly"
              onClick={() => setDateGranularity('quarterly')}
              icon={QuarterlyIcon}
            />
          </div>
        )}
      </div>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className={styles.chartWrapper}>
          <BarsChart
            data={chartData}
            height={250}
            dataKeys={dataKeys}
            specialGradient={barGradient}
            showLabel={false}
            isStack={false}
            dataProperties={dataProperties}
            xKey="usageDate"
            isDateChart
            barWidth={60}
            xTickFormat={dateGranularity}
            focusToStart={true}
            axisSettings={{
              yTickFormatter: (value: number) => numStrAbriviaionByDisplayMetric(value, value, DisplayMetricTypes.COST),
            }}
          />
        </div>
      )}
    </div>
  );
};

export default WasteTrackerChart;
